import { useState, useEffect } from "react";

const AVG_SPEED = { Truck: 55, Bus: 50, Car: 80, "Two-Wheeler": 65, Other: 60 };

// India bounding box — positions outside this are clearly in the sea or abroad
const IND = { latMin:6.5, latMax:37.5, lngMin:67.0, lngMax:97.5 };

// Great-circle bearing from point A → B (degrees, 0=N, 90=E, 180=S, 270=W)
function bearingBetween(lat1, lng1, lat2, lng2) {
  const toRad = d => (d * Math.PI) / 180;
  const toDeg = r => (r * 180) / Math.PI;
  const dLng  = toRad(lng2 - lng1);
  const φ1    = toRad(lat1), φ2 = toRad(lat2);
  const y     = Math.sin(dLng) * Math.cos(φ2);
  const x     = Math.cos(φ1)  * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Bearing → lat/lng unit step vectors (degrees per km)
function bearingToDelta(bearingDeg, lat) {
  const latK = 1 / 111;
  const lngK = 1 / (111 * Math.cos((lat * Math.PI) / 180));
  const rad  = (bearingDeg * Math.PI) / 180;
  return { dlat: Math.cos(rad) * latK, dlng: Math.sin(rad) * lngK };
}

function simulateOne(vehicle) {
  const history = vehicle.tollHistory; // newest first (index 0 = most recent)
  if (!history?.length) return null;

  // Most recent toll that has real GPS coordinates
  const lastIdx  = history.findIndex(t => t.lat && t.lng);
  if (lastIdx === -1) return null;
  const lastToll = history[lastIdx];

  const elapsedH = (Date.now() - new Date(lastToll.timestamp).getTime()) / 3600000;
  // Skip if < 3 min (still at plaza) or > 6 hours (too uncertain; likely parked)
  if (elapsedH < 0.05 || elapsedH > 6) return null;

  // Derive actual travel direction from the two most recent geolocated tolls.
  // Lane direction codes (E=Entry, X=Exit, W=West lane) are toll gate types,
  // NOT compass directions — using them causes vehicles to drift into the sea.
  let bearing = null;
  for (let i = lastIdx + 1; i < history.length; i++) {
    const prev = history[i];
    if (
      prev.lat && prev.lng &&
      (Math.abs(prev.lat - lastToll.lat) > 0.001 || Math.abs(prev.lng - lastToll.lng) > 0.001)
    ) {
      bearing = bearingBetween(prev.lat, prev.lng, lastToll.lat, lastToll.lng);
      break;
    }
  }

  // Cannot safely estimate direction without at least 2 geolocated tolls — don't simulate
  if (bearing === null) return null;

  const speed  = AVG_SPEED[vehicle.type] || 60;
  // Cap at 100 km — keeps marker near the highway corridor instead of drifting into the sea
  const distKm = Math.min(elapsedH * speed, 100);

  const { dlat, dlng } = bearingToDelta(bearing, lastToll.lat);

  // Gentle lateral sway perpendicular to travel direction — capped at 2 km max
  const { dlat: slat, dlng: slng } = bearingToDelta((bearing + 90) % 360, lastToll.lat);
  const sway = Math.sin(elapsedH * Math.PI * 1.5) * Math.min(distKm * 0.008, 2);

  const lat = lastToll.lat + dlat * distKm + slat * sway;
  const lng = lastToll.lng + dlng * distKm + slng * sway;

  // Drop if position landed outside India — vehicle would be in the sea or abroad
  if (lat < IND.latMin || lat > IND.latMax || lng < IND.lngMin || lng > IND.lngMax) return null;

  return {
    lat, lng,
    distKm:       Math.round(distKm),
    elapsedH,
    lastTollName: lastToll.plaza,
    direction:    Math.round(bearing) + "°",
    isEstimated:  true,
  };
}

export function useSimulatedPositions(vehicles) {
  const [positions, setPositions] = useState({});

  useEffect(() => {
    function tick() {
      const next = {};
      (vehicles || []).forEach(v => {
        if (!v.liveData || v.noRecentData || !v.tollHistory?.length) return;
        const pos = simulateOne(v);
        if (pos) next[v.id] = pos;
      });
      setPositions(next);
    }

    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [vehicles]);

  return positions;
}
